import React from 'react';
import { Sheet } from '../types';
import { sheetHasMultipleLayers } from '../logic/LayerLogic';
import { useLayerSwitch } from '../context/LayerSwitchContext';
import { LayerSlideNumber } from './LayerSlideNumber';
import { NumberCounter } from './NumberCounterComponent';
import { layerTimeDisplayNode } from '../utils/layerTimeDisplay';

interface LayerAwareCounterProps {
  sheet?: Sheet;
  value: number;
  isNegative?: boolean;
  duration?: number;
  animateFromZero?: boolean;
  suppressTooltip?: boolean;
  className?: string;
  groupId?: string;
  suffix?: string;
  showTimeConversion?: boolean;
  bridgePercentModeTransition?: boolean;
  displayAsTime?: boolean;
  timeDisplayTag?: 'strong' | 'span';
  formatDisplay?: (value: number, isNegative: boolean) => React.ReactNode;
}

/** sheet あり: LayerSlideNumber を常時マウント（1 層時は slideEnabled=false）。多層時スライド + カウントアニメ */
export const LayerAwareCounter: React.FC<LayerAwareCounterProps> = ({
  sheet,
  value,
  isNegative = false,
  duration = 200,
  animateFromZero = false,
  suppressTooltip,
  className,
  groupId,
  suffix,
  showTimeConversion,
  bridgePercentModeTransition,
  displayAsTime,
  timeDisplayTag,
  formatDisplay,
}) => {
  const { layerSwitchDirection, layerSwitchNonce } = useLayerSwitch();

  if (sheet) {
    const transitioning = layerSwitchDirection !== 0;
    const multiLayer = sheetHasMultipleLayers(sheet);
    const slideEnabled = multiLayer || transitioning;

    return (
      <LayerSlideNumber
        value={value}
        isNegative={isNegative}
        direction={layerSwitchDirection}
        switchNonce={layerSwitchNonce}
        slideEnabled={slideEnabled}
        duration={duration}
        animateFromZero={animateFromZero}
        suppressTooltip={suppressTooltip}
        showTimeConversion={showTimeConversion}
        suffix={suffix}
        className={className}
        displayAsTime={!!displayAsTime}
        timeDisplayTag={timeDisplayTag}
        formatDisplay={formatDisplay && !displayAsTime ? formatDisplay : undefined}
      />
    );
  }

  if (formatDisplay) {
    return formatDisplay(value, isNegative) as React.ReactElement;
  }

  if (displayAsTime) {
    return layerTimeDisplayNode(value, isNegative, timeDisplayTag ?? 'strong');
  }

  return (
    <NumberCounter
      value={value}
      isNegative={isNegative}
      duration={duration}
      animateFromZero={animateFromZero}
      suppressTooltip={suppressTooltip}
      className={className}
      groupId={groupId}
      suffix={suffix}
      showTimeConversion={showTimeConversion}
      bridgePercentModeTransition={bridgePercentModeTransition}
    />
  );
};
